import { useCallback, useEffect } from 'react'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'

interface UseRouteHashProps {
    hash: string
    onHashRemoved?: () => void
    replace?: boolean
}

function getPathWithSearch(pathname: string, search: string) {
    return search ? `${pathname}?${search}` : pathname
}

export function useRouteHash({ hash, onHashRemoved, replace }: UseRouteHashProps) {
    const router = useRouter()
    const pathname = usePathname()
    const searchParams = useSearchParams()
    const search = searchParams.toString()

    const addHash = useCallback(() => {
        const pathWithSearch = getPathWithSearch(pathname, search)
        const urlWithHash = `${pathWithSearch}#${hash}`
        if (replace) {
            router.replace(urlWithHash, { scroll: false })
        } else {
            router.push(urlWithHash, { scroll: false })
        }
    }, [hash, pathname, replace, router, search])

    const removeHash = useCallback(() => {
        const pathWithSearch = getPathWithSearch(pathname, search)
        router.replace(pathWithSearch, { scroll: false })
    }, [pathname, router, search])

    useEffect(() => {
        if (!onHashRemoved || typeof window === 'undefined') return

        const handleHashChange = () => {
            if (!window.location.hash.includes(hash)) {
                onHashRemoved()
            }
        }

        window.addEventListener('hashchange', handleHashChange)

        return () => window.removeEventListener('hashchange', handleHashChange)
    }, [hash, onHashRemoved])

    return { addHash, removeHash }
}
